import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Row, Col, message } from 'antd';
import { useTheme } from '../../../context/ThemeContext';
import FilterPanel from '../filters/FilterPanel';
import MapContainer from './MapContainer';
import ResultsList from '../ResultsList';
import './MapPage.css';
import { API_BASE_URL as SHARED_API_BASE_URL } from '../../../utils/config';

const MapPage = () => {
  const { darkMode } = useTheme();
  const [mapData, setMapData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const mapRef = useRef(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showFilters, setShowFilters] = useState(true);
  const [showBkmAtms, setShowBkmAtms] = useState(false);
  const [bkmLoading, setBkmLoading] = useState(false);
  const { maptriksId } = useParams();
  const routerLocation = useLocation();
  
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const API_BASE_URL = SHARED_API_BASE_URL;

  const fetchFilteredData = async (filterParams = {}) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      
      if (filterParams.provinceId) queryParams.append('il', filterParams.provinceId);
      if (filterParams.districtId) queryParams.append('ilce', filterParams.districtId);
      if (filterParams.searchText) queryParams.append('lokName', filterParams.searchText);
      
      const complexFilters = [];
      
      if (filterParams.firmIds && filterParams.firmIds.length > 0) {
        filterParams.firmIds.forEach(firmId => {
          complexFilters.push({"field": "firma", "value": firmId});
        });
      }
      
      if (filterParams.projectId) {
        queryParams.append('proje', filterParams.projectId);
      } else {
        if (filterParams.activeProjectIds && filterParams.activeProjectIds.length > 0) {
          filterParams.activeProjectIds.forEach(projectId => {
            complexFilters.push({"field": "proje", "value": projectId});
          });
        }
        
        if (filterParams.passiveProjectIds && filterParams.passiveProjectIds.length > 0) {
          filterParams.passiveProjectIds.forEach(projectId => {
            complexFilters.push({"field": "proje", "value": projectId});
          });
        }
      }
      
      if (filterParams.categoryIds && filterParams.categoryIds.length > 0) {
        filterParams.categoryIds.forEach(categoryId => {
          complexFilters.push({"field": "kategori", "value": categoryId});
        });
      }
      
      if (filterParams.statusIds && filterParams.statusIds.length > 0) {
        filterParams.statusIds.forEach(statusId => {
          complexFilters.push({"field": "durum", "value": statusId});
        });
      }
      
      if (filterParams.notificationIds && filterParams.notificationIds.length > 0) {
        filterParams.notificationIds.forEach(notificationId => {
          complexFilters.push({"field": "bildirim", "value": notificationId});
        });
      }    

      if (filterParams.includedBanks && filterParams.includedBanks.length > 0) {
        filterParams.includedBanks.forEach(bankId => {
          complexFilters.push({"field": "mevcutbanka", "value": bankId});
        });
      }
      
      if (filterParams.excludedBanks && filterParams.excludedBanks.length > 0) {
        filterParams.excludedBanks.forEach(bankId => {
          complexFilters.push({"field": "namevcutbanka", "value": bankId});
        });
      }
      
      if (filterParams.segment && filterParams.segment.length > 0) {
        complexFilters.push({
          "field": "segment",
          "value": filterParams.segment
        });
      }
      
      if (filterParams.bkmAtm) {
        complexFilters.push({"field": "BkmOffSite", "value": 1});
        complexFilters.push({"field": "BkmOnSite", "value": 1});
      }
      
      const paddedOffArr = (filterParams.btmAtmOffsite && filterParams.btmAtmOffsite.length > 0)
        ? filterParams.btmAtmOffsite.map((id) => String(id).padStart(4, '0'))
        : [];
      const paddedOnArr = (filterParams.btmAtmOnsite && filterParams.btmAtmOnsite.length > 0)
        ? filterParams.btmAtmOnsite.map((id) => String(id).padStart(4, '0'))
        : [];
      const btmOffRaw = `btmAtmOffsite=${JSON.stringify(paddedOffArr)}`;
      const btmOnRaw = `btmAtmOnsite=${JSON.stringify(paddedOnArr)}`;
      
      if (complexFilters.length > 0) {
        queryParams.append('filters', JSON.stringify(complexFilters));
      }
      
      let finalUrl = `${API_BASE_URL}/atm/query?${queryParams.toString()}`;
      const btmParts = [btmOffRaw, btmOnRaw];
      finalUrl += (queryParams.toString() ? '&' : '') + btmParts.join('&');
      const response = await fetch(finalUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      return result.data || [];
      
    } catch (error) {
      console.error('API çağrısı başarısız:', error);
      message.error('Veri yüklenirken hata oluştu: ' + error.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setShowFilters(false);
      } else {
        setShowFilters(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadSelectedLocation = async () => {
      if (!maptriksId) return;

      const navSelected = routerLocation.state?.selectedLocation;
      if (navSelected?.lat && navSelected?.lng && mapRef.current) {
        mapRef.current.panTo({
          lat: parseFloat(navSelected.lat),
          lng: parseFloat(navSelected.lng)
        });
        if (navSelected.zoom) {
          mapRef.current.setZoom(navSelected.zoom);
        }
        setSelectedItemId(String(navSelected.id || maptriksId));
      }

      try {
        setLoading(true);
        const resp = await fetch(`${API_BASE_URL}/atm/query?lokName=${maptriksId}`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();
        const transformed = transformMapData(json.data || []);
        if (transformed.length > 0) {
          const item = transformed[0];
          setMapData(transformed);
          setSelectedItemId(String(item.id));
          setTimeout(() => {
            if (mapRef.current) {
              mapRef.current.openInfoWindow(item);
            }
          }, 400);
        }
      } catch (e) {
        console.error('Seçili lokasyon yüklenemedi:', e);
      } finally {
        setLoading(false);
      }
    };

    loadSelectedLocation();
  }, [maptriksId]);

  const transformMapData = (rawData) => {
    if (!Array.isArray(rawData)) return [];
    const uniqueMap = new Map();
    
    rawData.forEach((item, idx) => {
      const isBkm =
        (item.banka !== undefined || item.onsite !== undefined) &&
        (item.latitude !== undefined && item.longitude !== undefined);

      if (isBkm) {
        const lat = parseFloat(item.latitude) || 0;
        const lng = parseFloat(item.longitude) || 0;
        const id = `bkm-${idx}-${lat}-${lng}`;
        
        const bkmKey = `bkm-${lat}-${lng}-${item.banka || ''}-${item.onsite || ''}`;
        if (!uniqueMap.has(bkmKey)) {
          uniqueMap.set(bkmKey, {
            id: id,
            bkm: true,
            banka: item.banka || 'Bilinmeyen Banka',
            onsite: item.onsite || '',
            offsite: item.offsite || '',
            address: item.address || '',
            name: `${item.banka || 'BKM ATM'}${item.onsite ? ` (${item.onsite})` : ''}`,
            coordinates: { lat, lng },
            durum_name: '',
            segment: 0,
            kategori: 0,
            kategori_name: '',
            lokasyonResim: item.lokasyonResim || item.resim,
            resim: item.resim || item.lokasyonResim,
          });
        }
      } else {
        const maptriksID = item.MaptriksID || item.id;
        if (maptriksID && !uniqueMap.has(`lok-${maptriksID}`)) {
          uniqueMap.set(`lok-${maptriksID}`, {
            id: maptriksID,
            maptriksID: maptriksID,
            name: item.Name || item.name || 'Bilinmeyen Lokasyon',
            address: item.Adres || item.address || '',
            coordinates: {
              lat: parseFloat(item.Enlem) || 0,
              lng: parseFloat(item.Boylam) || 0,
            },
            firma: item.firma,
            durum: item.durum,
            durum_name: item.durum_name || 'Bilinmeyen Durum',
            segment: item.segment || item.Segment || 0,
            lokasyonResim: item.lokasyonResim,
            kategori: item.kategori || 0,
            kategori_name: item.kategori_name || 0,
            bildirim: item.bildirim || item.Bildirim || 0,
            distance: item.distance || 0,
            banka: item.banka,
            onsite: item.onsite,
            offsite: item.offsite,
          });
        }
      }
    });
    
    return Array.from(uniqueMap.values())
      .filter((item) =>
        item.coordinates.lat !== 0 &&
        item.coordinates.lng !== 0 &&
        !isNaN(item.coordinates.lat) &&
        !isNaN(item.coordinates.lng)
      );
  };

  const handleSearch = async () => {
    const rawData = await fetchFilteredData(filters);
    const transformedData = transformMapData(rawData);
    setMapData(transformedData);
  };

  const handleToggleBkm = () => {
    setShowBkmAtms((v) => !v);
  };

  const handleLocationSelect = (location) => {
    setSelectedItemId(location.id); 
    if (mapRef.current) {
      mapRef.current.panTo(location.coordinates);
      mapRef.current.setZoom(15);
    }
  };

  const handleDownload = async (format) => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      if (filters.provinceId) queryParams.append('il', filters.provinceId);
      if (filters.districtId) queryParams.append('ilce', filters.districtId);
      if (filters.searchText) queryParams.append('lokName', filters.searchText);
      if (filters.dateFrom) queryParams.append('ilktar', filters.dateFrom);
      if (filters.dateTo) queryParams.append('sontar', filters.dateTo);
      
      const complexFilters = [];
      
      if (filters.firmIds?.length > 0) {
        filters.firmIds.forEach(firmId => {
          complexFilters.push({"field": "firma", "value": firmId});
        });
      }
      
      if (filters.projectId) {
        queryParams.append('proje', filters.projectId);
      } else {
        if (filters.activeProjectIds?.length > 0) {
          filters.activeProjectIds.forEach(projectId => {
            complexFilters.push({"field": "proje", "value": projectId});
          });
        }
        
        if (filters.passiveProjectIds?.length > 0) {
          filters.passiveProjectIds.forEach(projectId => {
            complexFilters.push({"field": "proje", "value": projectId});
          });
        }
      }
      
      if (filters.categoryIds?.length > 0) {
        filters.categoryIds.forEach(categoryId => {
          complexFilters.push({"field": "kategori", "value": categoryId});
        });
      }
      
      if (filters.statusIds?.length > 0) {
        filters.statusIds.forEach(statusId => {
          complexFilters.push({"field": "durum", "value": statusId});
        });
      }
      
      if (filters.notificationIds?.length > 0) {
        filters.notificationIds.forEach(notificationId => {
          complexFilters.push({"field": "bildirim", "value": notificationId});
        });
      }
      
      if (filters.includedBanks?.length > 0) {
        filters.includedBanks.forEach(bankId => {
          complexFilters.push({"field": "mevcutbanka", "value": bankId});
        });
      }
      
      if (filters.excludedBanks?.length > 0) {
        filters.excludedBanks.forEach(bankId => {
          complexFilters.push({"field": "namevcutbanka", "value": bankId});
        });
      }
      
      if (filters.segment !== null && filters.segment !== undefined) {
        queryParams.append('segment', filters.segment);
      }
      
      if (filters.bkmAtm) {
        complexFilters.push({"field": "BkmOffSite", "value": 1});
        complexFilters.push({"field": "BkmOnSite", "value": 1});
      }
      
      if (complexFilters.length > 0) {
        queryParams.append('filters', JSON.stringify(complexFilters));
      }
      
      if (format === 'excel') {
        queryParams.append('excel', 'true');
      } else if (format === 'kmz') {
        queryParams.append('kmz', 'true');
      }
      
      // <a href> ile doğrudan gezinme Authorization başlığı taşımaz; /atm/query
      // korunduğu için dosya fetch ile (apiAuth başlığı ekler) alınıp blob'dan indirilir.
      const url = `${API_BASE_URL}/atm/query?${queryParams.toString()}`;
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blobUrl = URL.createObjectURL(await resp.blob());
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Lokasyonlar_${new Date().toISOString().slice(0, 10)}.${format === 'excel' ? 'xlsx' : 'kmz'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);

      message.success(`${format.toUpperCase()} dosyası indiriliyor...`);
    } catch (error) {
      console.error('İndirme işlemi başarısız:', error);
      message.error('Dosya indirilirken bir hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`map-page ${darkMode ? 'dark-theme' : 'light-theme'}`}>
    <Row gutter={isMobile ? [0, 0] : [16, 16]} style={{ height: '100%' }}>
      {/* Left Column - Filters and Results */}
      {(!isMobile || showFilters) && (
        <Col 
          xs={24} 
          md={8} 
          lg={6} 
          style={{ 
            height: isMobile ? '50vh' : '100vh', 
            overflowY: 'auto', 
            background: darkMode ? '#222' : '#fff', 
            borderRight: isMobile ? 'none' : '1px solid #eee',
            borderBottom: isMobile ? '1px solid #eee' : 'none',
            position: isMobile ? 'relative' : 'static',
            zIndex: isMobile ? 1000 : 'auto'
          }}
        >
          <div style={{ minHeight: isMobile ? 'auto' : '100vh', display: 'flex', flexDirection: 'column' }}>
            <FilterPanel 
              onFilterChange={handleFilterChange}
              onSearch={handleSearch}
              onDownload={handleDownload}
              loading={loading}
              darkMode={darkMode}
              isMobile={isMobile}
            />
            {mapData.length > 0 && (
              <div style={{ 
                flex: 1, 
                marginTop: isMobile ? 8 : 16, 
                overflow: 'hidden', 
                display: 'flex', 
                flexDirection: 'column' 
              }}>
                <div style={{ fontWeight: 600, marginBottom: 8, fontSize: isMobile ? '13px' : '14px' }}>
                  Sonuçlar ({mapData.length})
                </div>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  <ResultsList 
                    data={mapData} 
                    onSelect={handleLocationSelect}
                    loading={loading}
                    darkMode={darkMode}
                    selectedItemId={selectedItemId}
                    isBkmMode={(filters?.btmAtmOffsite?.length > 0) || (filters?.btmAtmOnsite?.length > 0)}
                    isMobile={isMobile}
                  />
                </div>
              </div>
            )}
          </div>
        </Col>
      )}
      
      {/* Mobile Filter Toggle Button */}
      {isMobile && (
        <button
          onClick={() => setShowFilters(!showFilters)}
          style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            zIndex: 1001,
            backgroundColor: darkMode ? '#1f1f1f' : '#fff',
            border: `1px solid ${darkMode ? '#303030' : '#d9d9d9'}`,
            borderRadius: '6px',
            padding: '8px 12px',
            fontSize: '13px',
            fontWeight: '500',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: darkMode ? '#fff' : '#333'
          }}
        >
          {showFilters ? '✕ Filtreleri Gizle' : '☰ Filtreleri Göster'}
        </button>
      )}
      
      {/* Right Column - Map */}
      <Col xs={24} md={16} lg={18} style={{ height: isMobile && showFilters ? '50vh' : '100vh' }}>
        <div className="map-container-wrapper" style={{ height: '100%', position: 'relative' }}>
          <button
            onClick={handleToggleBkm}
            title="Haritada görünen alandaki offsite/BKM ATM'leri banka ikonlarıyla göster (harita hareket ettikçe güncellenir)"
            style={{
              position: 'absolute', top: 10, right: 10, zIndex: 1001,
              backgroundColor: showBkmAtms ? '#1677ff' : (darkMode ? '#1f1f1f' : '#fff'),
              color: showBkmAtms ? '#fff' : (darkMode ? '#fff' : '#333'),
              border: `1px solid ${darkMode ? '#303030' : '#d9d9d9'}`,
              borderRadius: 6, padding: '8px 12px', fontSize: 13, fontWeight: 600,
              cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
          >
            {bkmLoading ? '⏳ Yükleniyor…' : (showBkmAtms ? 'Offsite Katmanı: Açık' : 'Offsite Katmanı: Kapalı')}
          </button>
          <MapContainer 
            data={mapData} 
            darkMode={darkMode}
            selectedItemId={selectedItemId}
            onSelectLocation={handleLocationSelect}
            ref={mapRef}
            isMobile={isMobile}
            showBkmAtms={showBkmAtms}
          />
        </div>
      </Col>
    </Row>
  </div>
  );
};

export default MapPage;